import { useToast, ToastProvider, Button } from '@/shared/components/ui';
import styles from './ToastsPage.module.css';

// Componente interno que usa el hook
const ToastsDemo = () => {
    const { showToast } = useToast();

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Toasts</h1>
                <p className={styles.description}>
                    Sistema de notificaciones Toast para mostrar mensajes temporales.
                    Auto-dismiss configurable, 4 tipos y posición fija.
                </p>
            </div>

            {/* Tipos de Toast */}
            <section className={styles.section}>
                <h2>Tipos de Toast</h2>
                <p>Toast soporta 4 tipos visuales con iconos automáticos.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button
                            variant="primary"
                            onClick={() => showToast({
                                type: 'info',
                                message: 'Esta es una notificación informativa'
                            })}
                        >
                            Mostrar Info
                        </Button>
                        <code className={styles.code}>
                            {`showToast({ 
  type: 'info', 
  message: 'Notificación informativa' 
})`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="success"
                            onClick={() => showToast({
                                type: 'success',
                                message: '¡Operación completada exitosamente!'
                            })}
                        >
                            Mostrar Success
                        </Button>
                        <code className={styles.code}>
                            {`showToast({ 
  type: 'success', 
  message: '¡Exitoso!' 
})`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="secondary"
                            onClick={() => showToast({
                                type: 'warning',
                                message: 'Advertencia: Revisa los datos antes de continuar'
                            })}
                        >
                            Mostrar Warning
                        </Button>
                        <code className={styles.code}>
                            {`showToast({ 
  type: 'warning', 
  message: 'Advertencia...' 
})`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            variant="danger"
                            onClick={() => showToast({
                                type: 'error',
                                message: 'Error: No se pudo completar la operación'
                            })}
                        >
                            Mostrar Error
                        </Button>
                        <code className={styles.code}>
                            {`showToast({ 
  type: 'error', 
  message: 'Error al guardar' 
})`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Duración */}
            <section className={styles.section}>
                <h2>Duración Personalizada</h2>
                <p>Por defecto los toasts se auto-cierran en 3 segundos. Puedes personalizarlo.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Button
                            size="sm"
                            onClick={() => showToast({
                                type: 'info',
                                message: 'Toast rápido (1 segundo)',
                                duration: 1000
                            })}
                        >
                            1 segundo
                        </Button>
                        <code className={styles.code}>
                            {`duration: 1000  // 1s`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            size="sm"
                            onClick={() => showToast({
                                type: 'info',
                                message: 'Toast normal (3 segundos)',
                                duration: 3000
                            })}
                        >
                            3 segundos (default)
                        </Button>
                        <code className={styles.code}>
                            {`duration: 3000  // 3s (default)`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Button
                            size="sm"
                            onClick={() => showToast({
                                type: 'info',
                                message: 'Toast largo (5 segundos)',
                                duration: 5000
                            })}
                        >
                            5 segundos
                        </Button>
                        <code className={styles.code}>
                            {`duration: 5000  // 5s`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Múltiples Toasts */}
            <section className={styles.section}>
                <h2>Múltiples Toasts</h2>
                <p>Puedes mostrar varios toasts al mismo tiempo. Se apilan verticalmente.</p>

                <div className={styles.example}>
                    <Button
                        onClick={() => {
                            showToast({ type: 'info', message: 'Primera notificación' });
                            setTimeout(() => showToast({ type: 'success', message: 'Segunda notificación' }), 200);
                            setTimeout(() => showToast({ type: 'warning', message: 'Tercera notificación' }), 400);
                        }}
                    >
                        Mostrar 3 Toasts
                    </Button>

                    <code className={styles.code}>
                        {`showToast({ type: 'info', message: 'Primera' });
setTimeout(() => showToast({ type: 'success', message: 'Segunda' }), 200);
setTimeout(() => showToast({ type: 'warning', message: 'Tercera' }), 400);`}
                    </code>
                </div>
            </section>

            {/* Ejemplos de Uso */}
            <section className={styles.section}>
                <h2>Ejemplos de Uso</h2>

                <div className={styles.exampleGroup}>
                    <h3>Después de Guardar Datos</h3>
                    <code className={styles.code}>
                        {`const handleSave = async () => {
  try {
    await saveUser(data);
    showToast({ 
      type: 'success', 
      message: 'Usuario guardado correctamente' 
    });
  } catch (error) {
    showToast({ 
      type: 'error', 
      message: 'Error al guardar el usuario' 
    });
  }
};`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Validación de Formulario</h3>
                    <code className={styles.code}>
                        {`const handleSubmit = (e) => {
  e.preventDefault();
  
  if (!email.includes('@')) {
    showToast({ 
      type: 'warning', 
      message: 'Por favor ingresa un email válido' 
    });
    return;
  }
  
  // Continuar con el submit...
};`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Información de Sistema</h3>
                    <code className={styles.code}>
                        {`useEffect(() => {
  showToast({ 
    type: 'info', 
    message: 'Sincronizando datos...', 
    duration: 2000 
  });
  
  syncData();
}, []);`}
                    </code>
                </div>
            </section>

            {/* Configuración */}
            <section className={styles.section}>
                <h2>Cómo Usar</h2>

                <div className={styles.setupGuide}>
                    <h3>1. Agregar el ToastProvider</h3>
                    <p>Envuelve tu aplicación con el ToastProvider:</p>
                    <code className={styles.code}>
                        {`// App.tsx o main.tsx
import { ToastProvider } from '@/shared/components/ui';

function App() {
  return (
    <ToastProvider>
      {/* Tu aplicación */}
    </ToastProvider>
  );
}`}
                    </code>

                    <h3>2. Usar el Hook</h3>
                    <p>Dentro de cualquier componente hijo:</p>
                    <code className={styles.code}>
                        {`import { useToast } from '@/shared/components/ui';

function MyComponent() {
  const { showToast } = useToast();
  
  return (
    <button onClick={() => showToast({ 
      type: 'success', 
      message: '¡Hecho!' 
    })}>
      Click me
    </button>
  );
}`}
                    </code>
                </div>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props de showToast</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Default</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>type</code></td>
                                <td><code>'success' | 'error' | 'warning' | 'info'</code></td>
                                <td>-</td>
                                <td>Tipo de notificación (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>message</code></td>
                                <td><code>string</code></td>
                                <td>-</td>
                                <td>Mensaje a mostrar (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>duration</code></td>
                                <td><code>number</code></td>
                                <td><code>3000</code></td>
                                <td>Duración en milisegundos</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { ToastProvider, useToast } from '@/shared/components/ui';`}
                </code>
            </section>
        </div>
    );
};

// Wrapper con Provider para la página
export const ToastsPage = () => {
    return (
        <ToastProvider>
            <ToastsDemo />
        </ToastProvider>
    );
};
